import { Component, ErrorInfo, ReactNode } from 'react'
import './ErrorBoundary.css'

/**
 * Stops a single render error from white-screening the whole platform.
 *
 * Before this existed, one null numeric field anywhere in the tree took down
 * the entire app — sidebar included — with a blank page and no message.
 * Wrap the app shell once, and each view individually, so a broken screen
 * degrades to a recoverable panel instead of taking its neighbours with it.
 */

interface Props {
  children: ReactNode
  /** Shown in the heading, e.g. "Payment Run". Omit for the app shell. */
  label?: string
  /** Called when the user clicks "Back to Today" (app shell only). */
  onReset?: () => void
}

interface State {
  error: Error | null
  info: ErrorInfo | null
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info })
    // Keep the stack in the console for support; nothing here is user-facing.
    console.error('[DirectAuto] render error', this.props.label ?? 'app shell', error, info)
  }

  private reset = () => {
    this.setState({ error: null, info: null })
    this.props.onReset?.()
  }

  render() {
    const { error, info } = this.state
    if (!error) return this.props.children

    const where = this.props.label ? `The ${this.props.label} screen` : 'DirectAuto'

    return (
      <div className="eb-panel" role="alert">
        <div className="eb-icon" aria-hidden="true">!</div>
        <h2>{where} ran into a problem</h2>
        <p className="eb-lead">
          Nothing you were doing has been lost from the database — this screen just
          couldn’t finish drawing. Try it again, and if it keeps happening send it
          through with the details below.
        </p>

        <div className="eb-actions">
          <button className="eb-btn primary" onClick={this.reset}>
            {this.props.label ? 'Try this screen again' : 'Back to Today'}
          </button>
          <button className="eb-btn" onClick={() => window.location.reload()}>
            Reload DirectAuto
          </button>
        </div>

        <details className="eb-details">
          <summary>Technical details</summary>
          <pre>
            {error.message}
            {info?.componentStack ? `\n${info.componentStack.trim()}` : ''}
          </pre>
        </details>
      </div>
    )
  }
}
